import { useEffect, useRef, type ReactNode } from "react";

/**
 * A dialog's refusal or error: the alert, focused and scrolled into view
 * when it appears and again whenever `focusKey` changes (a second refusal).
 *
 * Claude's walk of 2026-09-23 (gairm/lms#236): a refused Assign rendered its
 * alert at the top of a dialog scrolled down to the Assign button — out of
 * sight — and the button, disabled while the request ran, had already
 * dropped focus to <body> with the modal still open. Focus on the alert puts
 * the keyboard and the screen reader where the answer is, inside the dialog,
 * and the next Tab goes on to the form.
 */
export function DialogAlert({ children, focusKey, className = "", bare = false }: { children: ReactNode; focusKey?: unknown; className?: string; /** The child is its own alert (ErrorState): this only takes focus. */ bare?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: "nearest" });
  }, [focusKey]);
  return (
    <div ref={ref} role={bare ? undefined : "alert"} tabIndex={-1} data-dialog-alert className={className}>
      {children}
    </div>
  );
}
